import { Question } from '../types';

// 技术标签映射
const technicalTagMap = {
  'AI': [
    { keywords: ['机器学习', '深度学习', 'neural', 'ML'], tag: 'Machine Learning' },
    { keywords: ['transformer', 'LLM', '大语言模型'], tag: 'LLM' },
    { keywords: ['计算机视觉', 'CV', '图像识别'], tag: 'Computer Vision' },
    { keywords: ['NLP', '自然语言', '文本处理'], tag: 'NLP' }
  ],
  'Web3': [
    { keywords: ['智能合约', 'solidity', '合约开发'], tag: 'Smart Contract' },
    { keywords: ['区块链', '共识机制', '分布式账本'], tag: 'Blockchain' },
    { keywords: ['DeFi', '去中心化金融', '金融协议'], tag: 'DeFi' },
    { keywords: ['NFT', '非同质化代币', '数字藏品'], tag: 'NFT' }
  ]
};

// 应用标签映射
const applicationTagMap = {
  'AI': [
    { keywords: ['模型部署', '服务部署', 'inference'], tag: 'AI Deployment' },
    { keywords: ['数据处理', '数据清洗', 'pipeline'], tag: 'Data Engineering' },
    { keywords: ['模型优化', '性能调优', 'optimization'], tag: 'Model Optimization' }
  ],
  'Web3': [
    { keywords: ['DApp', '去中心化应用', '智能合约交互'], tag: 'DApp Development' },
    { keywords: ['钱包集成', 'Web3.js', 'ethers.js'], tag: 'Wallet Integration' },
    { keywords: ['跨链', '互操作性', 'bridge'], tag: 'Cross-chain' }
  ]
};

// 创新标签映射
const innovationTagMap = {
  'AI': [
    { keywords: ['AI治理', '伦理', '安全性'], tag: 'AI Governance' },
    { keywords: ['新算法', '创新架构', '突破性'], tag: 'Algorithm Innovation' },
    { keywords: ['多模态', '跨模态', 'multimodal'], tag: 'Multimodal AI' }
  ],
  'Web3': [
    { keywords: ['Layer2', '扩容方案', 'scaling'], tag: 'Scaling Solutions' },
    { keywords: ['DAO', '治理机制', '投票系统'], tag: 'DAO Innovation' },
    { keywords: ['零知识证明', 'ZK', 'privacy'], tag: 'Privacy Tech' }
  ]
};

// 从问题和答案中提取标签
function extractTags(questions: Question[], answers: number[], category: string) {
  const correctAnswers = questions.map((q, i) => ({
    question: q,
    isCorrect: q.correctAnswer === answers[i]
  }));

  // 提取技术标签
  const technicalTags = new Set<string>();
  correctAnswers.forEach(({ question, isCorrect }) => {
    if (isCorrect && question.type === 'theoretical') {
      const tagMap = technicalTagMap[question.category as keyof typeof technicalTagMap] || [];
      tagMap.forEach(({ keywords, tag }) => {
        if (keywords.some(k => 
          question.question.toLowerCase().includes(k.toLowerCase()) ||
          question.options[question.correctAnswer].toLowerCase().includes(k.toLowerCase())
        )) {
          technicalTags.add(tag);
        }
      });
    }
  });

  // 提取应用标签
  const applicationTags = new Set<string>();
  correctAnswers.forEach(({ question, isCorrect }) => {
    if (isCorrect && question.type === 'application') {
      const tagMap = applicationTagMap[question.category as keyof typeof applicationTagMap] || [];
      tagMap.forEach(({ keywords, tag }) => {
        if (keywords.some(k => 
          question.question.toLowerCase().includes(k.toLowerCase()) ||
          question.options[question.correctAnswer].toLowerCase().includes(k.toLowerCase())
        )) {
          applicationTags.add(tag);
        }
      });
    }
  });

  // 提取创新标签
  const innovationTags = new Set<string>();
  correctAnswers.forEach(({ question, isCorrect }) => {
    if (isCorrect) {
      const tagMap = innovationTagMap[question.category as keyof typeof innovationTagMap] || [];
      tagMap.forEach(({ keywords, tag }) => {
        if (keywords.some(k => 
          question.question.toLowerCase().includes(k.toLowerCase()) ||
          question.options[question.correctAnswer].toLowerCase().includes(k.toLowerCase())
        )) {
          innovationTags.add(tag);
        }
      });
    }
  });

  return {
    technicalTags: Array.from(technicalTags),
    applicationTags: Array.from(applicationTags),
    innovationTags: Array.from(innovationTags)
  };
}

// 计算各维度得分
function calculateDimensionScores(questions: Question[], answers: number[]) {
  const technicalQuestions = questions.filter(q => q.type === 'theoretical');
  const applicationQuestions = questions.filter(q => q.type === 'application');
  
  const technicalCorrect = technicalQuestions.filter((q, i) => 
    answers[questions.indexOf(q)] === q.correctAnswer
  ).length;
  
  const applicationCorrect = applicationQuestions.filter((q, i) => 
    answers[questions.indexOf(q)] === q.correctAnswer
  ).length;

  // 创新得分基于整体表现和难度
  const innovationScore = Math.round(
    (questions.filter((q, i) => 
      answers[i] === q.correctAnswer && q.difficulty === 'advanced'
    ).length / Math.max(1, questions.filter(q => q.difficulty === 'advanced').length)) * 100
  );

  return {
    technicalScore: Math.round((technicalCorrect / Math.max(1, technicalQuestions.length)) * 100),
    applicationScore: Math.round((applicationCorrect / Math.max(1, applicationQuestions.length)) * 100),
    innovationScore
  };
}

export function processAssessmentResult(questions: Question[], answers: number[], category: 'AI' | 'Web3' | 'both') {
  const scores = calculateDimensionScores(questions, answers);
  const tags = extractTags(questions, answers, category);
  const yearsOfExperience = Math.floor(scores.technicalScore / 20);
  
  return {
    ...scores,
    ...tags,
    yearsOfExperience,
    userProfile: {
      industry: {
        primary: 'tech', // Default to tech if not specified
      },
      useCases: [],
      experience: {
        years: yearsOfExperience,
        roles: ['Learner'], // Default role
      },
      interests: [category],
      learningStyle: 'practical' as const,
      goals: ['Improve knowledge']
    }
  };
}